import { useMutation } from '@apollo/client'
import usePrice from '@commerce/product/use-price'
import { Cart } from '@commerce/types/cart'
import type { Product, ProductVariant } from '@commerce/types/product'
import cartLinesAddMutation, {
  cartAddMultipleLinesVars,
} from '@commerce/utils/mutations/cart-lines-add'
import { Close, FancyArrowLeft } from '@components/icons'
import { ProductPaintSelection } from '@components/product'
import { SelectedOptions } from '@components/product/helpers'
import { Button, useUI } from '@components/ui'
import {
  SAMPLE_GARDEN_POT_UPSELL_PRODUCTS,
  checkUpsellProductExists,
  getUpsellProdutsByProduct,
} from '@lib/cart'
import { pinterestAddToCartEvent } from '@lib/events/addToCart'
import {
  PRODUCT_CUSTOM_ATTRIBUTES_KEYS,
  findMetafieldValueByKey,
} from '@lib/helpers/general'
import cn from 'clsx'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
interface props {
  options: ProductVariant[]
  currencyCode: string
  handleBack: () => void
  selectedSibling: Product
  cart: Cart | null
  parentProduct?: Product
  prismicContent?: object
  variantMetafields?: object
}
const ProductByGardenPotProcess: FC<props> = ({
  handleBack,
  options,
  currencyCode,
  selectedSibling,
  parentProduct,
  prismicContent,
  cart,
}) => {
  const { PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY } = PRODUCT_CUSTOM_ATTRIBUTES_KEYS
  const { t, i18n } = useTranslation('common')
  const { locale } = useRouter()
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const [loading, setLoading] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)
  const [totalLitres, setTotalLitres] = useState(0)
  const name = selectedSibling.name
  const { price: formattedPrice } = usePrice({
    amount: totalPrice!,
    currencyCode: currencyCode!,
    suffix: `${t('vat-as-applicable')}`,
  })

  useEffect(() => {
    let subTotals = 0
    let litres = 0
    Object.keys(selectedOptions).forEach((key) => {
      const variant = options.find((o) => o.id === key)
      const quantity = Number(selectedOptions[key])
      if (variant && variant.id) {
        const quantityWithoutUnit = variant.name?.trim()?.replace(/\D/g, '')
        const quantityIsInML = variant.name
          ?.trim()
          ?.toLowerCase()
          .includes('ml')
        const litre = quantityIsInML
          ? Number(quantityWithoutUnit) / 1000
          : quantityWithoutUnit
        if (litre) {
          litres = litres + quantity * Number(litre)
        }
        if (variant && variant.price) {
          subTotals = subTotals + quantity * Number(variant.price)
        }
      }
    })
    setTotalLitres(litres)
    setTotalPrice(subTotals)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions])

  const {
    setUpsellViewContent,
    setSidebarView,
    openSidebar,
    openCartNotification,
    setUpsellProductType,
    // closeCartNotification,
  } = useUI()
  // const { hits, sendEvent } = useHits()
  const [addItem] = useMutation(cartLinesAddMutation, {
    onCompleted() {
      // sendEvent('conversion', hits, 'Added To Cart', {
      //   eventSubtype: 'addToCart',
      //   product: Object.keys(selectedOptions).map((key) => {
      //     return {
      //       currency: selectedSibling?.price?.currencyCode,
      //       product: selectedSibling?.name,
      //       productId: selectedSibling?.id,
      //       variant: selectedSibling?.variants?.find(
      //         (variant) => variant?.id === key
      //       )?.name,
      //       variantId: key,
      //       sku: selectedSibling?.variants?.find(
      //         (variant) => variant?.id === key
      //       )?.sku,
      //       price: selectedSibling?.variants?.find(
      //         (variant) => variant?.id === key
      //       )?.price,
      //       quantity: Number(selectedOptions[key]),
      //     }
      //   }),
      // })
      pinterestAddToCartEvent(
        Object.keys(selectedOptions).map((key) => {
          return {
            product_id: selectedSibling?.id || '',
            product_variant:
              selectedOptions?.size ||
              selectedSibling?.variants?.[0]?.name ||
              '',
            product_variant_id:
              selectedSibling?.variants?.find(
                (variant) =>
                  variant?.name?.toLowerCase() ===
                  selectedOptions?.size?.toString().toLowerCase()
              )?.id ||
              selectedSibling?.variants?.[0]?.id ||
              '',
            product_price:
              selectedSibling?.variants?.find(
                (variant) =>
                  variant?.name?.toLowerCase() ===
                  selectedOptions?.size?.toString().toLowerCase()
              )?.price ||
              selectedSibling?.variants?.[0]?.price ||
              0,
            product_quantity: Number(selectedOptions[key]),
            currency: selectedSibling?.price?.currencyCode || 'AUD',
          }
        })
      )
      const language = locale?.split('-')?.[0]
      const upsellProducts = getUpsellProdutsByProduct(selectedSibling!)
      const upsellBrushExists = checkUpsellProductExists(cart!, upsellProducts)
      if (upsellBrushExists.length > 0)
        openCartNotification(
          `${totalLitres}L ${language === 'en' ? `of ` : ''} ${name} ${t(
            'added-to-cart'
          )}`
        )

      if (upsellBrushExists.length === 0) {
        setUpsellViewContent(
          `${totalLitres}L ${language === 'en' ? `of ` : ''} ${name} ${t(
            'added-to-cart'
          )}`,
          SAMPLE_GARDEN_POT_UPSELL_PRODUCTS
        )
        setUpsellProductType(null)
        setSidebarView('UPSELL_VIEW')
        openSidebar()
      }
      setLoading(false)
    },
    onError() {
      setLoading(false)
    },
  })

  const addToCart = async () => {
    setLoading(true)
    addItem({
      variables: cartAddMultipleLinesVars(
        Object.keys(selectedOptions).map((key) => {
          const variant = options.find((o) => o.id === key)
          const recipe = findMetafieldValueByKey(variant?.metafields!, 'recipe')
          const customAttributes = [
            {
              key: PARENT_PRODUCT_CUSTOM_ATTRIBUTE_KEY,
              value: parentProduct?.handle || '',
            },
            // {
            //   key: PRODUCT_TYPE_CUSTOM_ATTRIBUTE_KEY,
            //   value: parentProduct?.productType,
            // },
            // {
            //   key: COLOUR_CODE_CUSTOM_ATTRIBUTE_KEY,
            //   value: parentProduct?.colourCode,
            // },
          ]
          if (recipe) {
            customAttributes.push({
              key: '_recipe',
              value: recipe,
            })
          }
          return {
            merchandiseId: key,
            quantity: Number(selectedOptions[key]),
            attributes: customAttributes,
          }
        }),
        locale
      ),
      context: { locale },
    })
  }
  return (
    <div className="flex h-full flex-col justify-between lg:pl-9 ">
      <div className="flex flex-1 flex-col ">
        <div className="flex justify-between">
          <button onClick={handleBack}>
            <FancyArrowLeft />
          </button>
          <button onClick={handleBack}>
            <Close />
          </button>
        </div>
        <div className="mt-4 flex-1 md:mt-9 ">
          <ProductPaintSelection
            surfaceArea={0}
            options={options}
            currencyCode={currencyCode}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            isGardenPotPaint
            prismicContent={prismicContent}
            numberOfCoats={''}
          />
        </div>
      </div>
      <div
        className={cn('flex flex-col', {
          'pt-4': !i18n.exists('incl-vat-approx-detail'),
        })}
      >
        {i18n.exists('incl-vat-approx-detail') && (
          <div className="body-2 pt-2 pb-4">{`${t(
            'incl-vat-approx-detail'
          )}`}</div>
        )}
        <Button
          aria-label={`${t('add-to-cart')}`}
          type="button"
          width="100%"
          onClick={addToCart}
          loading={loading}
          disabled={Object.keys(selectedOptions).length === 0 || loading}
        >
          {totalPrice == 0
            ? `${t('make-your-selection')}`
            : `${t('garden-pot-paint')} - ${formattedPrice} ${
                locale === 'en-gb' ? `(incl. GST / VAT)` : ''
              } ${t('add-to-cart')} `}
        </Button>
      </div>
    </div>
  )
}
export default ProductByGardenPotProcess
